import { z } from 'zod'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { getStoryBySlug } from '@cinch-nx/data-storyblok'

import { CarReviewV2PageStoryContent } from './types/car-review-page'
import { reduceRelatedReviews } from './utils'

import {
  traceFunction,
  tagError,
  getTraceId,
  setTag,
} from '@core/datadog-server'
import { storySchema } from './utils/zod/car-review-v2-schemas'
import { zodErrorSerialiser } from './utils/zod/utils'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { Vehicle } from '@cinch-nx/shared-types'

const instrumentedGetStoryBySlug = traceFunction(
  'getStoryBySlug',
  getStoryBySlug,
)

const requiredParams = z.object({
  make: z.string(),
  model: z.string(),
  slug: z.string(),
})

export async function getStaticProps({
  preview = false,
  params,
}: GetStaticPropsContext) {
  setTag('request.params', params)

  const ddMeta = {
    traceId: getTraceId(),
    traceTime: Date.now(),
  }

  try {
    const { make, model, slug } = requiredParams.parse(params)

    const isInsideStoryblok = preview

    const storyArgs: Parameters<typeof getStoryBySlug>[1] = {
      version: isInsideStoryblok ? 'draft' : 'published',
      resolve_relations: 'CarReviewTemplateV2.relatedReviews',
      cv: Date.now(),
    }

    const story = await instrumentedGetStoryBySlug<CarReviewV2PageStoryContent>(
      `car-reviews-v2/${make}/${model}/${slug}`,
      storyArgs,
    )

    const parsedResults = storySchema.safeParse(story)

    let zodErrors: string[] = []

    if (parsedResults.success === false) {
      zodErrors = zodErrorSerialiser(parsedResults.error)
    }

    const relatedReviews = []

    if (story?.content.relatedReviews) {
      relatedReviews.push(...reduceRelatedReviews(story.content.relatedReviews))
    }

    const response = await fetch(
      `${readFromEnv(Env.SearchServiceUrl)}/vehicles?make=${
        story.content.make
      }&model=${story.content.model}`,
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch vehicles: ${response.statusText}`)
    }
    const { vehicleListings } = await response.json()

    const vehiclesInStock: boolean = vehicleListings.some(
      (vehicle: Vehicle) =>
        vehicle.make.toLowerCase() === story.content.make.toLowerCase() &&
        vehicle.model?.toLowerCase() === story.content.model.toLowerCase(),
    )
    setTag('response.statusCode', 200)

    return {
      props: {
        ddMeta,
        story: {
          ...story,

          content: {
            ...story.content,
            relatedReviews,
          },
        },
        preview,
        validationErrors: zodErrors,
        generatedAt: Date.now(),
        areVehiclesInStock: vehiclesInStock,
      },
      revalidate: 30, // In seconds
    }
  } catch (error) {
    console.error(error)

    if (typeof error === 'string' || error instanceof Error) {
      tagError(error)
    }

    if (error instanceof z.ZodError) {
      tagError(JSON.stringify(error.flatten().fieldErrors))
    }

    return {
      notFound: true,
      props: {
        ddMeta,
      },
      revalidate: 30,
    }
  }
}

export type CarReviewsV2FeatureProps = InferGetStaticPropsType<
  typeof getStaticProps
>
